/** @file useServerList: a server list (projects or puppets), newest first, with reload and error. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { asPose3DError } from '../store/asPose3DError';
import { errorText } from '../store/errorText';

/** The entries (null while loading), the load error and `reload()`. */
export type ServerList<T> = { entries: T[] | null; error: string | null; reload: () => Promise<void> };

/**
 * Calls `load` on mount and whenever its identity changes (callers memoize it on what it depends on), sorted by
 * `updatedAt` newest first; answers for an older request are dropped. A null `load` (no server) stays empty.
 */
export function useServerList<T extends { updatedAt: number }>(load: (() => Promise<T[]>) | null): ServerList<T> {
  const [entries, setEntries] = useState<T[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const request = useRef(0);
  const reload = useCallback(async (): Promise<void> => {
    if (load === null) return;
    request.current += 1;
    const mine = request.current;
    try {
      const list = await load();
      if (mine !== request.current) return;
      setEntries([...list].sort((a, b) => b.updatedAt - a.updatedAt));
      setError(null);
    } catch (e) {
      if (mine === request.current) setError(errorText(asPose3DError(e)));
    }
  }, [load]);
  useEffect(() => {
    setEntries(null);
    void reload();
  }, [reload]);
  return { entries, error, reload };
}
